import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  Divider,
  Grid,
  IconButton,
  InputAdornment,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  Add as AddIcon,
  AttachMoney as AttachMoneyIcon,
  DeleteOutline as DeleteIcon,
  DragIndicator as DragIndicatorIcon,
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
  HistoryEdu as HistoryIcon,
} from "@mui/icons-material";
import Gate from "../../../components/common/Gate";
import { useToggle } from "../../../contexts/FeatureToggleContext";

const CARD_TOGGLE_FLAGS = {
  marketValue: "property.section.marketValue",
  acquisitionHistory: "property.section.acquisitionHistory",
};

const FIELD_SX = {
  "& .MuiOutlinedInput-root": {
    backgroundColor: "#f8f9fa",
    borderRadius: "8px",
    fontSize: "14px",
    "& fieldset": { borderColor: "#e2e8f0" },
    "&:hover fieldset": { borderColor: "#cbd5e0" },
    "&.Mui-focused fieldset": { borderColor: "#4299e1", borderWidth: "1px" },
    "& input": { padding: "12px 14px" },
  },
};

const CARD_WRAP_SX = {
  borderRadius: 3,
  border: "1px solid rgba(0,0,0,0.10)",
  boxShadow: "0 10px 30px rgba(0,0,0,0.06)",
  bgcolor: "background.paper",
  overflow: "hidden",
  mb: 1.5,
};

const HEADER_SX = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  px: 1.5,
  py: 0.75,
  minHeight: 40,
  backgroundColor: "#E5E7EB",
  borderBottom: "1px solid #CBD5E1",
  cursor: "pointer",
};

const TITLE_SX = {
  fontWeight: 700,
  fontSize: 12,
  lineHeight: 1.1,
  letterSpacing: "0.04em",
  textTransform: "uppercase",
  color: "#334155",
};

const HANDLE_SLOT_SX = { width: 16, mr: 0.25, flexShrink: 0 };

const DragHandle = () => (
  <DragIndicatorIcon sx={{ fontSize: 16, color: "#94A3B8", cursor: "grab", mr: 0.25, flexShrink: 0 }} />
);

const DELETE_BUTTON_SX = {
  color: "#64748B",
  p: 0.35,
  "&:hover": { backgroundColor: "#fee2e2", color: "#ef4444" },
};

const getCardState = (cardKey) => {
  try {
    const stored = localStorage.getItem("property:cardState");
    if (!stored) return true;
    const parsed = JSON.parse(stored);
    return parsed[cardKey] ?? true;
  } catch {
    return true;
  }
};

const saveCardState = (cardKey, value) => {
  try {
    const stored = localStorage.getItem("property:cardState");
    const parsed = stored ? JSON.parse(stored) : {};
    parsed[cardKey] = value;
    localStorage.setItem("property:cardState", JSON.stringify(parsed));
  } catch {}
};

const generateId = () =>
  `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;

const toDateInputValue = (v) => {
  if (!v) return "";
  const s = String(v).trim();
  // Already YYYY-MM-DD
  const m = s.match(/^(\d{4}-\d{2}-\d{2})/);
  if (m) return m[1];
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? "" : d.toISOString().slice(0, 10);
};

const PropertyHistoryCards = ({ propertyInfo, updatePropertyInfo, allCardsCollapsed, onCollapseChange, setAllSignal, requestSetAllCards }) => {
  const rawSales = propertyInfo?.details?.sales;
  const purchaseHistory = Array.isArray(rawSales)
    ? rawSales.map((s, i) => ({
        id: s.id || `sale_${i}_${(s.date || "") + "_" + (s.price ?? "")}`,
        date: toDateInputValue(s.date),
        price: s.price != null ? String(s.price) : "",
      }))
    : [];

  const writeSales = (next) => {
    const sales = next.map(({ date, price }) => ({
      date: date || "",
      price: price === "" || price == null ? null : Number(price) || price,
    }));
    updatePropertyInfo("details.sales", sales);
    const latest = sales[sales.length - 1] || {};
    updatePropertyInfo("details.sale", { date: latest.date || "", price: latest.price ?? null });
  };

  const marketOn = useToggle(CARD_TOGGLE_FLAGS.marketValue);
  const acquisitionOn = useToggle(CARD_TOGGLE_FLAGS.acquisitionHistory);

  const [isMarketCollapsed, setIsMarketCollapsed] = useState(() => getCardState("marketValue"));
  const [isAcquisitionCollapsed, setIsAcquisitionCollapsed] = useState(() => getCardState("acquisitionHistory"));

  useEffect(() => {
    const states = {};
    if (marketOn) states.marketValue = isMarketCollapsed;
    if (acquisitionOn) states.acquisitionHistory = isAcquisitionCollapsed;
    onCollapseChange?.(states);
  }, [marketOn, acquisitionOn, isMarketCollapsed, isAcquisitionCollapsed, onCollapseChange]);

  useEffect(() => {
    if (!setAllSignal || setAllSignal.target == null) return;
    const t = !!setAllSignal.target;
    setIsMarketCollapsed(t); saveCardState("marketValue", t);
    setIsAcquisitionCollapsed(t); saveCardState("acquisitionHistory", t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setAllSignal?.n]);

  const [cardOrder, setCardOrder] = useState(["marketValue", "acquisitionHistory"]);
  const dragKeyRef = useRef(null);
  const dragOverKeyRef = useRef(null);
  const localAllCollapsed = isMarketCollapsed && isAcquisitionCollapsed;
  const isDraggable = typeof allCardsCollapsed === "boolean" ? allCardsCollapsed : localAllCollapsed;

  const handleDragStart = (key) => (e) => {
    dragKeyRef.current = key;
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", key);
    e.currentTarget.style.opacity = "0.5";
  };
  const handleDragEnter = (key) => {
    if (dragKeyRef.current === key) return;
    dragOverKeyRef.current = key;
  };
  const handleDragEnd = (e) => {
    e.currentTarget.style.opacity = "1";
    const from = dragKeyRef.current;
    const to = dragOverKeyRef.current;
    dragKeyRef.current = null;
    dragOverKeyRef.current = null;
    if (!from || !to || from === to) return;
    setCardOrder((prev) => {
      const next = [...prev];
      const i = next.indexOf(from);
      const j = next.indexOf(to);
      if (i === -1 || j === -1) return prev;
      const [moved] = next.splice(i, 1);
      next.splice(j, 0, moved);
      return next;
    });
  };
  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
  };

  const handleAddEntry = useCallback(() => {
    writeSales([...purchaseHistory, { id: generateId(), date: "", price: "" }]);
  }, [purchaseHistory]);

  const handleDeleteEntry = useCallback(
    (id) => writeSales(purchaseHistory.filter((e) => e.id !== id)),
    [purchaseHistory]
  );

  const handleUpdateEntry = useCallback(
    (id, field, value) =>
      writeSales(purchaseHistory.map((e) => (e.id === id ? { ...e, [field]: value } : e))),
    [purchaseHistory]
  );

  const toggleMarket = () =>
    setIsMarketCollapsed((prev) => {
      const next = !prev;
      saveCardState("marketValue", next);
      return next;
    });

  const toggleAcquisition = () =>
    setIsAcquisitionCollapsed((prev) => {
      const next = !prev;
      saveCardState("acquisitionHistory", next);
      return next;
    });

  const renderMarketCard = () => (
    <Box sx={CARD_WRAP_SX}>
      <Box sx={HEADER_SX} onClick={toggleMarket} onDoubleClick={(e) => { e.stopPropagation(); requestSetAllCards?.(!isMarketCollapsed); }} title="Double-click to open/close all">
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
          {isDraggable ? <DragHandle /> : <Box sx={HANDLE_SLOT_SX} />}
          <AttachMoneyIcon sx={{ fontSize: 16, color: "#10B981" }} />
          <Typography sx={TITLE_SX}>Current Market Value</Typography>
        </Box>
        <IconButton size="small" sx={{ color: "#64748B", p: 0.35 }}>
          {isMarketCollapsed ? <ExpandMoreIcon sx={{ fontSize: 16 }} /> : <ExpandLessIcon sx={{ fontSize: 16 }} />}
        </IconButton>
      </Box>
      {!isMarketCollapsed ? (
        <Box sx={{ p: 2 }}>
          <Grid container spacing={2.5}>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Current Market Value"
                value={propertyInfo?.details?.propertyInfo?.current_market_value || ""}
                onChange={(e) =>
                  updatePropertyInfo("details.propertyInfo.current_market_value", e.target.value)
                }
                variant="outlined"
                sx={FIELD_SX}
                InputLabelProps={{ shrink: true }}
                InputProps={{
                  startAdornment: <InputAdornment position="start">$</InputAdornment>,
                }}
              />
            </Grid>
          </Grid>
        </Box>
      ) : null}
    </Box>
  );

  const renderAcquisitionCard = () => (
    <Box sx={CARD_WRAP_SX}>
      <Box sx={HEADER_SX} onClick={toggleAcquisition} onDoubleClick={(e) => { e.stopPropagation(); requestSetAllCards?.(!isAcquisitionCollapsed); }} title="Double-click to open/close all">
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
          {isDraggable ? <DragHandle /> : <Box sx={HANDLE_SLOT_SX} />}
          <HistoryIcon sx={{ fontSize: 16, color: "#0EA5E9" }} />
          <Typography sx={TITLE_SX}>Acquisition History</Typography>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          {!isAcquisitionCollapsed && (
            <Button
              size="small"
              startIcon={<AddIcon sx={{ fontSize: 14 }} />}
              onClick={(e) => {
                e.stopPropagation();
                handleAddEntry();
              }}
              sx={{
                fontSize: 11,
                fontWeight: 600,
                textTransform: "none",
                color: "#4299e1",
                py: 0.25,
                px: 1,
                minWidth: 0,
                "&:hover": { backgroundColor: "rgba(66,153,225,0.08)" },
              }}
            >
              Add Entry
            </Button>
          )}
          <IconButton size="small" sx={{ color: "#64748B", p: 0.35 }}>
            {isAcquisitionCollapsed ? <ExpandMoreIcon sx={{ fontSize: 16 }} /> : <ExpandLessIcon sx={{ fontSize: 16 }} />}
          </IconButton>
        </Box>
      </Box>
      {!isAcquisitionCollapsed ? (
        <Box sx={{ p: 2 }}>
          {purchaseHistory.length === 0 ? (
            <Typography sx={{ color: "#94A3B8", fontSize: 13 }}>
              No acquisition entries. Click "Add Entry" to begin.
            </Typography>
          ) : (
            purchaseHistory.map((entry, index) => (
              <Box key={entry.id}>
                <Grid container spacing={2.5} alignItems="center">
                  <Grid item xs={12} md={5}>
                    <TextField
                      fullWidth
                      label="Date Acquired"
                      type="date"
                      value={entry.date || ""}
                      onChange={(e) => handleUpdateEntry(entry.id, "date", e.target.value)}
                      variant="outlined"
                      sx={FIELD_SX}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  <Grid item xs={12} md={5}>
                    <TextField
                      fullWidth
                      label="Purchase Price"
                      value={entry.price || ""}
                      onChange={(e) => handleUpdateEntry(entry.id, "price", e.target.value)}
                      variant="outlined"
                      sx={FIELD_SX}
                      InputLabelProps={{ shrink: true }}
                      InputProps={{
                        startAdornment: <InputAdornment position="start">$</InputAdornment>,
                      }}
                    />
                  </Grid>
                  <Grid item xs="auto" sx={{ display: "flex", alignItems: "center" }}>
                    <Tooltip title="Delete Entry">
                      <IconButton size="small" onClick={() => handleDeleteEntry(entry.id)} sx={DELETE_BUTTON_SX}>
                        <DeleteIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Tooltip>
                  </Grid>
                </Grid>
                {index < purchaseHistory.length - 1 && <Divider sx={{ my: 2 }} />}
              </Box>
            ))
          )}
        </Box>
      ) : null}
    </Box>
  );

  const renderCard = (key) => {
    if (key === "marketValue") return renderMarketCard();
    if (key === "acquisitionHistory") return renderAcquisitionCard();
    return null;
  };

  return (
    <>
      {cardOrder.map((key) => (
        <Gate key={key} flag={CARD_TOGGLE_FLAGS[key]}>
          <Box
            draggable={isDraggable}
            onDragStart={isDraggable ? handleDragStart(key) : undefined}
            onDragEnter={isDraggable ? () => handleDragEnter(key) : undefined}
            onDragEnd={handleDragEnd}
            onDragOver={handleDragOver}
          >
            {renderCard(key)}
          </Box>
        </Gate>
      ))}
    </>
  );
};

export default PropertyHistoryCards;
